/**
 * disposal_registrations — Disposal portal self-registration table (DEC-156).
 *
 * Phase 16 / Task 16.1 — quick 260715-a1x.
 *
 * Mirrors operator_registrations (0059 / DEC-148) with DISPOSAL-specific columns:
 *   - license_number / state — soft-lookup against disposal_facilities catalog
 *   - facility_name / facility_address — registrant-supplied context
 *   - matched_facility_id — nullable FK (SET NULL on facility delete)
 *   - license_match_status — 4-state enum (MATCHED / UNMATCHED / NOT_PROVIDED /
 *     MANUALLY_VERIFIED). UNMATCHED is accepted per DEC-156 §3 (many states
 *     have no license catalog yet — TX/NM/WY/ND wait on Phase 14).
 *
 * NO RLS: platform-scoped (no tenant context at self-registration time). The
 * migration (0063) intentionally does NOT REVOKE INSERT/UPDATE/DELETE from
 * app_user — same posture as 0059 / operator_registrations. Reads/writes go
 * through the platform-db plugin (see packages/api/src/plugins/platform-db.ts).
 *
 * Status flow (this task wires PENDING → EMAIL_VERIFIED only):
 *   PENDING_EMAIL_VERIFY → EMAIL_VERIFIED → APPROVED | REJECTED
 *   APPROVED and REJECTED are declared here for future 16.2+ tasks.
 *
 * UPPERCASE-on-write (DEC-147) for identifier fields — company_name,
 * contact_name, facility_name, facility_address, license_number — enforced at
 * the Zod boundary via upperCaseIdentifier (packages/shared/src/validation/
 * identifier.ts). Email is NOT uppercased (case-insensitive by RFC; uniqueness
 * via the LOWER(email) partial-unique index in migration 0063).
 *
 * Spec: master spec §27 (Disposal portal), §49 (DisposalCompany + facility
 * schemas), §50 (license lookup). DEC-156.
 */

import { pgTable, uuid, varchar, timestamp, text, index } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { disposalCompanies, disposalFacilities } from './disposals.js';
import { tenants, users } from './users.js';

/**
 * disposal_registrations — one row per disposal-company self-registration attempt.
 *
 * Partial-unique index on email WHERE status != 'REJECTED' (hand-written in
 * migration 0063; Drizzle pgTable options do not support partial unique
 * indexes) prevents a second active registration for the same email while
 * allowing re-registration after rejection.
 *
 * matched_facility_id: nullable FK to disposal_facilities. Populated when
 * license_match_status = 'MATCHED' via the disposal-license-lookup service
 * (packages/api/src/services/disposal-license-lookup.ts). ON DELETE SET NULL
 * so retiring a facility from the catalog doesn't orphan the registration.
 */
export const disposalRegistrations = pgTable(
  'disposal_registrations',
  {
    id: uuid('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    // RFC 5321 max length. Stored lowercase (Zod trims + lowercases).
    email: varchar('email', { length: 320 }).notNull(),
    // DEC-147 — UPPERCASE via upperCaseIdentifier at the Zod boundary.
    companyName: varchar('company_name', { length: 200 }).notNull(),
    contactName: varchar('contact_name', { length: 200 }).notNull(),
    phone: varchar('phone', { length: 50 }).notNull(),

    // DISPOSAL-specific columns (DEC-156 §3-6).
    licenseNumber: varchar('license_number', { length: 50 }),
    state: varchar('state', { length: 2 }),
    facilityName: varchar('facility_name', { length: 200 }),
    facilityAddress: varchar('facility_address', { length: 500 }),
    matchedFacilityId: uuid('matched_facility_id').references(() => disposalFacilities.id, {
      onDelete: 'set null',
    }),
    // CHECK constraint in migration: MATCHED | UNMATCHED | NOT_PROVIDED | MANUALLY_VERIFIED.
    licenseMatchStatus: varchar('license_match_status', { length: 32 })
      .notNull()
      .default('NOT_PROVIDED'),

    // CHECK constraint in migration: ('PENDING_EMAIL_VERIFY','EMAIL_VERIFIED','APPROVED','REJECTED').
    status: varchar('status', { length: 32 }).notNull().default('PENDING_EMAIL_VERIFY'),
    emailVerifiedAt: timestamp('email_verified_at', { withTimezone: true }),

    // ── Phase 16 / Task 16.2 (DEC-157) — approval-time fields ────────────────
    // Added by migration 0064. Mirror operator_registrations approval-time
    // shape (0060) with two DISPOSAL-specific additions:
    //   - manualVerificationNote: admin's note when approving UNMATCHED /
    //     NOT_PROVIDED registrations (min 10 chars enforced Zod-side, prose
    //     preserved case-as-typed per DEC-147 notes/comments exemption).
    //   - linkedDisposalCompanyId: FK to the disposal_companies row created
    //     on approve (no operator equivalent — the operator flow doesn't
    //     create a disposal_companies sibling).
    // All NULLABLE — set at APPROVED / REJECTED transitions.
    manualVerificationNote: text('manual_verification_note'),
    approvedAt: timestamp('approved_at', { withTimezone: true }),
    approvedByUserId: uuid('approved_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    rejectedAt: timestamp('rejected_at', { withTimezone: true }),
    rejectedByUserId: uuid('rejected_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    rejectedReason: text('rejected_reason'),
    linkedTenantId: uuid('linked_tenant_id').references(() => tenants.id, {
      onDelete: 'set null',
    }),
    linkedUserId: uuid('linked_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    linkedDisposalCompanyId: uuid('linked_disposal_company_id').references(
      () => disposalCompanies.id,
      { onDelete: 'set null' },
    ),

    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Supports magic-link issue lookup: WHERE email = $1 AND status = 'PENDING_EMAIL_VERIFY'.
    index('disposal_registrations_email_status_idx').on(t.email, t.status),
    // Phase 16 / Task 16.2 (DEC-157) — status-filter index for the admin queue's
    // default status=EMAIL_VERIFIED filter. Mirror operator_registrations_status_idx
    // (from 0060). Migration 0064 pins it.
    index('disposal_registrations_status_idx').on(t.status, t.createdAt),
    // FK support index for admin queue join to matched facility (16.2+).
    // Partial (WHERE matched_facility_id IS NOT NULL) hand-written in migration 0063.
    // Modeled here as a comment only (Drizzle pgTable partial indexes not supported).
    // Note: the partial-unique index (email WHERE status != 'REJECTED') is likewise
    // hand-written in migration 0063.
    // Note (16.2): the partial linked_tenant_id index (WHERE linked_tenant_id
    // IS NOT NULL) is hand-written in migration 0064 — Drizzle pgTable partial
    // indexes not supported.
  ],
);

export type DisposalRegistration = typeof disposalRegistrations.$inferSelect;
export type NewDisposalRegistration = typeof disposalRegistrations.$inferInsert;
